import { Box, Button, Chip, IconButton, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const AdminUsers = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const token = localStorage.getItem('token');

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      if (search) params.set('search', search);
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/adminusers?${params.toString()}`, {
        headers: { token },
      });
      const data = await res.json();
      if (data.success) {
        setAllUsers(data.data);
        setTotalCount(data.pagination?.total || 0);
      }
    } catch (e) {
      console.error('AdminUsers fetch error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Delete Admin user
  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/adminusers/${id}`, {
      headers: { token },
      method: 'DELETE',
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        getData();
      }
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const columns = [
    {
      field: 'username',
      headerName: 'Email',
      flex: 1,
      minWidth: 100,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 0.6,
      minWidth: 110,
      valueGetter: (params) => {
        const r = Number(params?.row?.role || 0);
        if (r === 3) return 'Coordinator';
        if (r === 2) return 'Admin';
        return params?.row?.roleName || 'Custom role';
      },
    },
    {
      field: 'CountryNames',
      headerName: 'Destination',
      flex: 1,
      minWidth: 100,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'View',
      minWidth: 90,
      renderCell: (cellValues) => {
        const isCoordinator = Number(cellValues.row.role) === 3;
        if (!isCoordinator) return null;
        return (
          <Link to={`/adminusers/${cellValues.row.id}/coordinator-view`}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.blueAccent[700] },
              }}
              variant="contained"
              size="small"
              startIcon={<VisibilityIcon />}
            >
              View
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Edit',
      renderCell: (cellValues) => {
        return (
          <Link to={`/adminusers/${cellValues.row.id}`}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.greenAccent[700] },
              }}
              variant="contained"
            >
              <EditIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Delete',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Button
            sx={{
              backgroundColor: colors.redAccent[500],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.redAccent[600] },
            }}
            variant="contained"
            onClick={() => openDeleteDialog(cellValues.row.id)}
          >
            <DeleteIcon />
          </Button>
        );
      },
    },
  ];

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="ADMIN USERS" subtitle="Managing all Admin users" />

        <Box display="flex" gap="10px" flexWrap="wrap">
          <Link to={'/roles'}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.greenAccent[800],
                },
              }}
            >
              <VerifiedUserIcon sx={{ mr: '10px' }} />
              Roles & Permissions
            </Button>
          </Link>
          <Link to={'/adminusers/add'}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              <PersonOutlinedIcon sx={{ mr: '10px' }} />
              Add User
            </Button>
          </Link>
        </Box>
      </Box>

      <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center" m="10px 0 0 0">
        <TextField
          size="small"
          placeholder="Search by email, destination..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
      </Box>

      {search && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          <Chip
            label={`Search: ${search}`}
            size="small"
            onDelete={handleClearSearch}
            sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
          />
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: colors.primary[400],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={allUsers}
          columns={columns}
          getRowId={(row) => row.id}
          disableColumnFilter
          loading={loading}
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete admin user?"
        message="Are you sure you want to delete this admin user?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AdminUsers;
